<script>
import { mapActions, mapGetters } from 'vuex'
import { CandidateHaveBack } from '@/shared/constants/feedback/CandidateHaveBack'
import VuetifyColorClass from '@/shared/constants/core/VuetifyColourClass'
import baseMixin from '@/mixins/baseMixin'
import BaseButton from '@/components/_base-button.vue'
import featureFlagMixin from '@/mixins/featureFlagMixin'
import ErrorAlert from '@/components/general/error-alert.vue'
import EmptyStateBlock from '@/components/general/empty-state-block.vue'
import toasts from '@/services/toasts/index'

export default {
  name: 'BookingFeedbackModal',
  components: {
    ErrorAlert,
    BaseButton,
    EmptyStateBlock,
  },
  mixins: [baseMixin, featureFlagMixin],
  model: {
    prop: 'show',
    event: 'input',
  },
  props: {
    booking: {
      type: Object,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
  },
  data: function() {
    return {
      rating: 0,
      loadingUrl: false,
      savingFeedback: false,
      haveBack: CandidateHaveBack.PREFERRED, // Default haveBack value
      CandidateHaveBack,
      commentRules: [(v) => v.length <= 200],
      commentText: '',
      commentTextError: false,
      hasErrors: false,
      error: {},
      dialog: false,
      quickFeedbackSuccess: false,
      VuetifyColorClass,
    }
  },
  computed: {
    ...mapGetters({
      isSubmittingQuickFeedback: 'bookings/isSubmittingQuickFeedback',
    }),
    isOpen: {
      get() {
        return this.show
      },
      set(val) {
        if (!val) {
          this.handleClose()
          return
        }
        this.$emit('input', val)
      },
    },
    bookingId() {
      return this.booking?.id
    },
    feedback() {
      return this.booking?.feedback
    },
    candidate() {
      return this.booking?.candidate
    },
    formValid() {
      return !this.commentTextError && this.rating > 0
    },
    feedbackProvided() {
      return this.feedback?.provided || this.quickFeedbackSuccess
    },
  },
  methods: {
    ...mapActions({
      requestFeedbackFormUrl: 'bookings/requestFeedbackFormUrl',
      submitQuickFeedback: 'bookings/submitQuickFeedback',
    }),
    async requestExtendedFeedback() {
      this.rating = 0
      this.loadingUrl = true

      try {
        const response = await this.requestFeedbackFormUrl({
          bookingId: this.bookingId,
        })

        window.open(response.data.url, '_blank')
      } finally {
        this.loadingUrl = false
        this.resetFeedback()
      }
    },
    async saveFeedback() {
      this.clearError()
      this.savingFeedback = true

      const response = await this.submitQuickFeedback({
        bookingId: this.bookingId,
        data: {
          rating: this.parseRating(),
          haveBack: this.haveBack,
          comment: this.commentText,
        },
      })

      if (!response.isSuccess) {
        this.hasErrors = true
        this.error = response.error
      } else {
        this.quickFeedbackSuccess = true
        this.resetFeedback()
        this.$emit('feedbackProvided')
        toasts.success('Thank you for providing feedback')
        this.handleClose()
      }

      this.savingFeedback = false
    },
    parseRating() {
      let zeroIndexedRating = this.rating - 1

      // Fail safe rating values
      if (zeroIndexedRating < 0) {
        zeroIndexedRating = 0
      } else if (zeroIndexedRating > 4) {
        zeroIndexedRating = 4
      }

      return zeroIndexedRating
    },
    resetFeedback() {
      this.rating = 0
      this.haveBack = CandidateHaveBack.PREFERRED
      this.commentText = ''
      this.clearError()
      this.dialog = false
    },
    clearError() {
      this.error = {}
      this.hasErrors = false
    },
    commentTextErrorUpdate(hasError) {
      this.commentTextError = hasError
    },
    handleClose() {
      this.resetFeedback()
      this.$emit('input', false)
    },
  },
}
</script>

<template>
  <BaseModal v-model="isOpen" hide-header-divider hide-cancel :width="600">
    <template v-slot:header>
      <p :class="textClass.h6" class="mb-0 feedback-dialog-content">{{
        $t('feedback.quickFeedbackTitle')
      }}</p>
    </template>
    <template>
      <EmptyStateBlock
        v-if="feedbackProvided"
        icon="mdi-check-circle-outline"
        :title="$t('feedback.modal.providedTitle')"
        :description="$t('feedback.modal.providedDescription')"
      >
        <template v-slot:button>
          <div class="mt-4">
            <BaseButton lg @click="handleClose">
              <v-icon left> mdi-close </v-icon>
              {{ $t('general.close') }}
            </BaseButton>
          </div>
        </template>
      </EmptyStateBlock>
      <div v-else class="feedback-dialog-content">
        <div>
          <div class="text-center mb-2">
            <v-rating
              v-model="rating"
              background-color="purple lighten-3"
              color="purple"
              class="quick-feedback-rating"
              hover
              length="5"
            ></v-rating>
            <BaseButton
              secondary
              :loading="loadingUrl"
              :disabled="isSubmittingQuickFeedback"
              @click="requestExtendedFeedback"
              >{{ $t('feedback.extendedFeedbackBtn') }}</BaseButton
            >
          </div>
          <v-radio-group
            v-model="haveBack"
            class="preferred-radio-group"
            mandatory
            column
          >
            <template v-slot:label>
              <div>
                {{
                  $t('feedback.haveBackQuestionText', {
                    ...candidate,
                  })
                }}
              </div>
            </template>
            <v-radio
              :label="$t('feedback.haveBackPreferredOption')"
              :value="CandidateHaveBack.PREFERRED"
              :class="`preferred-radio-${CandidateHaveBack.PREFERRED}`"
            ></v-radio>
            <v-radio
              :label="$t('feedback.haveBackYesOption')"
              :value="CandidateHaveBack.YES"
              :class="`preferred-radio-${CandidateHaveBack.YES}`"
            ></v-radio>
            <v-radio
              :label="$t('feedback.haveBackNoOption')"
              :value="CandidateHaveBack.NO"
              :class="`preferred-radio-${CandidateHaveBack.NO}`"
            ></v-radio>
          </v-radio-group>
          <v-expand-transition>
            <v-textarea
              v-if="haveBack === CandidateHaveBack.NO"
              v-model="commentText"
              outlined
              :label="$t('feedback.haveBackCommentText')"
              :rules="commentRules"
              class="comment-textarea"
              counter
              @update:error="commentTextErrorUpdate"
            ></v-textarea>
          </v-expand-transition>
        </div>

        <ErrorAlert v-if="hasErrors" type="error" :error="error" class="ma-0" />
      </div>
    </template>

    <template v-slot:actions>
      <v-spacer></v-spacer>
      <BaseButton
        sm
        secondary
        :disabled="isSubmittingQuickFeedback"
        class="cancel-btn"
        @click="handleClose"
      >
        {{ $t('feedback.cancelQuickFeedbackBtn') }}
      </BaseButton>
      <BaseButton
        sm
        :loading="isSubmittingQuickFeedback"
        :disabled="!formValid"
        class="save-btn"
        @click="saveFeedback"
      >
        {{ $t('feedback.saveQuickFeedbackBtn') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>
